// Built-in Web Audio effects. Each builds a small node subgraph and exposes
// a stable input/output plus a live `update`. WAM plugins are handled separately.
import { Effect, EffectKind, EffectParam } from "../state/model";
import { uid } from "../util";
import { getInsertProfile, getInsertProfileVersion } from "./noise-reduction";

export interface EffectNode {
  input: AudioNode;
  output: AudioNode;
  update(params: EffectParam): void;
  dispose(): void;
}

export const EFFECT_DEFAULTS: Record<Exclude<EffectKind, "wam">, EffectParam> = {
  eq: { low: 0, mid: 0, high: 0, midFreq: 1000 },
  compressor: { threshold: -24, ratio: 4, attack: 0.003, release: 0.25, knee: 30 },
  delay: { time: 0.3, feedback: 0.35, mix: 0.25 },
  reverb: { decay: 2.2, mix: 0.25 },
  gain: { gain: 0 },
  lowpass: { cutoff: 6000, q: 0.7 },
  highpass: { cutoff: 120, q: 0.7 },
  saturator: { drive: 0.3, mix: 1, output: 0 },
  chorus: { rate: 1.2, depth: 6, mix: 0.4 },
  limiter: { threshold: -3, release: 0.12, output: 0 },
  width: { width: 1.3 },
  denoise: { strength: 0.7, smoothing: 0.5, mix: 1 },
  deverb: { decay: 1.2, damping: 0.5, reverb: -3, strength: 0.5, mix: 0.85 },
};

export const EFFECT_LABELS: Record<EffectKind, string> = {
  eq: "3-Band EQ",
  compressor: "Compressor",
  delay: "Delay",
  reverb: "Reverb",
  gain: "Gain / Trim",
  lowpass: "Low-pass Filter",
  highpass: "High-pass Filter",
  saturator: "Saturator",
  chorus: "Chorus",
  limiter: "Limiter",
  width: "Stereo Width",
  denoise: "Denoise (profile)",
  deverb: "Deverb (room echo)",
  wam: "WAM Plugin",
};

// Algorithmic latency (samples) the denoise worklet adds (NF-1, measured). Used
// for plugin-delay-compensation in offline render/export.
export const DENOISE_LATENCY = 1023;

// Versioned URL so a returning user doesn't run a stale cached worklet after we
// ship changes to it. Bump the query when public/denoise-worklet.js changes.
export const DENOISE_WORKLET_URL = "/denoise-worklet.js?v=3";

/** Register the denoise worklet on a (possibly offline) context. Returns whether
 *  it's available; if not, denoise inserts fall back to passthrough (no latency). */
export async function ensureDenoiseWorklet(ctx: BaseAudioContext): Promise<boolean> {
  try { await (ctx as any).audioWorklet.addModule(DENOISE_WORKLET_URL); return true; }
  catch { return false; }
}

// Same NF (1024) as the denoise worklet, so the same measured NF-1 latency applies.
export const DEVERB_LATENCY = 1023;

export const DEVERB_WORKLET_URL = "/deverb-worklet.js?v=2";

/** Register the deverb worklet on a (possibly offline) context. Returns whether
 *  it's available; if not, deverb inserts fall back to passthrough (no latency). */
export async function ensureDeverbWorklet(ctx: BaseAudioContext): Promise<boolean> {
  try { await (ctx as any).audioWorklet.addModule(DEVERB_WORKLET_URL); return true; }
  catch { return false; }
}

/** Total latency (samples) an fx chain introduces — the denoise and deverb insert(s). */
export function chainLatency(
  fx: { kind: EffectKind; enabled: boolean }[],
  denoiseReady: boolean,
  deverbReady = false,
): number {
  let n = 0;
  if (denoiseReady) n += fx.filter((f) => f.enabled && f.kind === "denoise").length * DENOISE_LATENCY;
  if (deverbReady) n += fx.filter((f) => f.enabled && f.kind === "deverb").length * DEVERB_LATENCY;
  return n;
}

function distortionCurve(drive: number): Float32Array {
  const n = 1024, curve = new Float32Array(n);
  const k = 1 + Math.max(0, drive) * 8; // 1..~9 pre-gain into tanh
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1;
    curve[i] = Math.tanh(k * x) / Math.tanh(k);
  }
  return curve;
}

export function makeEffect(kind: Exclude<EffectKind, "wam">): Effect {
  return {
    id: uid("fx"),
    kind,
    name: EFFECT_LABELS[kind],
    enabled: true,
    params: { ...EFFECT_DEFAULTS[kind] },
  };
}

function makeImpulse(ctx: BaseAudioContext, seconds: number, decay = 2.5): AudioBuffer {
  const rate = ctx.sampleRate;
  const len = Math.max(1, Math.floor(seconds * rate));
  const buf = ctx.createBuffer(2, len, rate);
  for (let ch = 0; ch < 2; ch++) {
    const data = buf.getChannelData(ch);
    // Seeded noise (not Math.random): the same settings always give the same
    // reverb, so exports are reproducible and chunked renders join seamlessly.
    let s = (0x9e3779b9 ^ (ch + 1) * 0x85ebca6b ^ Math.round(seconds * 1000) * 31 ^ Math.round(decay * 1000)) >>> 0;
    for (let i = 0; i < len; i++) {
      s = (s + 0x6d2b79f5) >>> 0; // mulberry32
      let t = Math.imul(s ^ (s >>> 15), 1 | s);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      const r = ((t ^ (t >>> 14)) >>> 0) / 4294967296;
      data[i] = (r * 2 - 1) * Math.pow(1 - i / len, decay);
    }
  }
  return buf;
}

const primed = new WeakSet<AudioParam>();
/** Set an effect parameter. The first set is exact (new nodes and offline
 *  renders start at the right value); later changes glide over ~12 ms so live
 *  slider moves don't produce zipper noise/clicks. */
function sp(ctx: BaseAudioContext, p: AudioParam, v: number) {
  if (!Number.isFinite(v)) return;
  if (!primed.has(p)) { primed.add(p); p.value = v; return; }
  p.cancelScheduledValues(ctx.currentTime);
  p.setTargetAtTime(v, ctx.currentTime, 0.012);
}

export function createEffectNode(ctx: BaseAudioContext, fx: Effect): EffectNode {
  const p = fx.params;
  switch (fx.kind) {
    case "gain": {
      const g = ctx.createGain();
      const node: EffectNode = {
        input: g,
        output: g,
        update: (pp) => { sp(ctx, g.gain, Math.pow(10, Number(pp.gain ?? 0) / 20)); },
        dispose: () => g.disconnect(),
      };
      node.update(p);
      return node;
    }
    case "eq": {
      const low = ctx.createBiquadFilter(); low.type = "lowshelf"; low.frequency.value = 250;
      const mid = ctx.createBiquadFilter(); mid.type = "peaking"; mid.Q.value = 0.9;
      const high = ctx.createBiquadFilter(); high.type = "highshelf"; high.frequency.value = 4000;
      low.connect(mid); mid.connect(high);
      const node: EffectNode = {
        input: low,
        output: high,
        update: (pp) => {
          sp(ctx, low.gain, Number(pp.low ?? 0));
          sp(ctx, mid.gain, Number(pp.mid ?? 0));
          sp(ctx, mid.frequency, Number(pp.midFreq ?? 1000));
          sp(ctx, high.gain, Number(pp.high ?? 0));
        },
        dispose: () => { low.disconnect(); mid.disconnect(); high.disconnect(); },
      };
      node.update(p);
      return node;
    }
    case "compressor": {
      const c = ctx.createDynamicsCompressor();
      const node: EffectNode = {
        input: c,
        output: c,
        update: (pp) => {
          sp(ctx, c.threshold, Number(pp.threshold ?? -24));
          sp(ctx, c.ratio, Number(pp.ratio ?? 4));
          sp(ctx, c.attack, Number(pp.attack ?? 0.003));
          sp(ctx, c.release, Number(pp.release ?? 0.25));
          sp(ctx, c.knee, Number(pp.knee ?? 30));
        },
        dispose: () => c.disconnect(),
      };
      node.update(p);
      return node;
    }
    case "delay": {
      const inGain = ctx.createGain();
      const dry = ctx.createGain();
      const wet = ctx.createGain();
      const out = ctx.createGain();
      const delay = ctx.createDelay(5);
      const fb = ctx.createGain();
      inGain.connect(dry); dry.connect(out);
      inGain.connect(delay); delay.connect(fb); fb.connect(delay);
      delay.connect(wet); wet.connect(out);
      const node: EffectNode = {
        input: inGain,
        output: out,
        update: (pp) => {
          sp(ctx, delay.delayTime, Number(pp.time ?? 0.3));
          sp(ctx, fb.gain, Math.min(0.95, Number(pp.feedback ?? 0.35)));
          const mix = Number(pp.mix ?? 0.25);
          sp(ctx, wet.gain, mix); sp(ctx, dry.gain, 1 - mix);
        },
        dispose: () => [inGain, dry, wet, out, delay, fb].forEach((n) => n.disconnect()),
      };
      node.update(p);
      return node;
    }
    case "reverb": {
      const inGain = ctx.createGain();
      const dry = ctx.createGain();
      const wet = ctx.createGain();
      const out = ctx.createGain();
      const conv = ctx.createConvolver();
      conv.buffer = makeImpulse(ctx, Number(p.decay ?? 2.2));
      inGain.connect(dry); dry.connect(out);
      inGain.connect(conv); conv.connect(wet); wet.connect(out);
      let lastDecay = Number(p.decay ?? 2.2);
      let irTimer: any = null;
      const node: EffectNode = {
        input: inGain,
        output: out,
        update: (pp) => {
          const mix = Number(pp.mix ?? 0.25);
          sp(ctx, wet.gain, mix); sp(ctx, dry.gain, 1 - mix);
          const d = Number(pp.decay ?? 2.2);
          if (Math.abs(d - lastDecay) > 0.01) {
            // regenerating the impulse is costly — wait until the slider settles
            lastDecay = d;
            clearTimeout(irTimer);
            irTimer = setTimeout(() => { conv.buffer = makeImpulse(ctx, lastDecay); }, 150);
          }
        },
        dispose: () => { clearTimeout(irTimer); [inGain, dry, wet, out, conv].forEach((n) => n.disconnect()); },
      };
      node.update(p);
      return node;
    }
    case "lowpass":
    case "highpass": {
      const f = ctx.createBiquadFilter();
      f.type = fx.kind === "lowpass" ? "lowpass" : "highpass";
      const node: EffectNode = {
        input: f, output: f,
        update: (pp) => { sp(ctx, f.frequency, Number(pp.cutoff ?? 1000)); sp(ctx, f.Q, Number(pp.q ?? 0.7)); },
        dispose: () => f.disconnect(),
      };
      node.update(p);
      return node;
    }
    case "saturator": {
      const inG = ctx.createGain();
      const dry = ctx.createGain();
      const wet = ctx.createGain();
      const out = ctx.createGain();
      const shaper = ctx.createWaveShaper();
      shaper.oversample = "4x";
      inG.connect(dry); dry.connect(out);
      inG.connect(shaper); shaper.connect(wet); wet.connect(out);
      let lastDrive = -1;
      const node: EffectNode = {
        input: inG, output: out,
        update: (pp) => {
          const drive = Number(pp.drive ?? 0.3);
          if (drive !== lastDrive) { (shaper as any).curve = distortionCurve(drive); lastDrive = drive; }
          const mix = Number(pp.mix ?? 1);
          sp(ctx, wet.gain, mix); sp(ctx, dry.gain, 1 - mix);
          sp(ctx, out.gain, Math.pow(10, Number(pp.output ?? 0) / 20));
        },
        dispose: () => [inG, dry, wet, out, shaper].forEach((n) => n.disconnect()),
      };
      node.update(p);
      return node;
    }
    case "chorus": {
      const inG = ctx.createGain();
      const dry = ctx.createGain();
      const wet = ctx.createGain();
      const out = ctx.createGain();
      const delay = ctx.createDelay(0.1);
      delay.delayTime.value = 0.025;
      const lfo = ctx.createOscillator();
      const lfoGain = ctx.createGain();
      lfo.connect(lfoGain); lfoGain.connect(delay.delayTime); lfo.start();
      inG.connect(dry); dry.connect(out);
      inG.connect(delay); delay.connect(wet); wet.connect(out);
      const node: EffectNode = {
        input: inG, output: out,
        update: (pp) => {
          sp(ctx, lfo.frequency, Number(pp.rate ?? 1.2));
          sp(ctx, lfoGain.gain, Number(pp.depth ?? 6) / 1000); // ms → s modulation depth
          const mix = Number(pp.mix ?? 0.4);
          sp(ctx, wet.gain, mix); sp(ctx, dry.gain, 1 - mix);
        },
        dispose: () => { try { lfo.stop(); } catch {} [inG, dry, wet, out, delay, lfo, lfoGain].forEach((n) => n.disconnect()); },
      };
      node.update(p);
      return node;
    }
    case "limiter": {
      const c = ctx.createDynamicsCompressor();
      c.ratio.value = 20; c.knee.value = 0; c.attack.value = 0.001;
      const out = ctx.createGain();
      c.connect(out);
      const node: EffectNode = {
        input: c, output: out,
        update: (pp) => {
          sp(ctx, c.threshold, Number(pp.threshold ?? -3));
          sp(ctx, c.release, Number(pp.release ?? 0.12));
          sp(ctx, out.gain, Math.pow(10, Number(pp.output ?? 0) / 20));
        },
        dispose: () => { c.disconnect(); out.disconnect(); },
      };
      node.update(p);
      return node;
    }
    case "width": {
      // Mid/side stereo widener: out = mid ± side*width.
      const inG = ctx.createGain();
      const split = ctx.createChannelSplitter(2);
      const merge = ctx.createChannelMerger(2);
      const lMid = ctx.createGain(), rMid = ctx.createGain(); lMid.gain.value = 0.5; rMid.gain.value = 0.5;
      const lSide = ctx.createGain(), rSide = ctx.createGain(); lSide.gain.value = 0.5; rSide.gain.value = -0.5;
      const mid = ctx.createGain(), side = ctx.createGain();
      const sideToL = ctx.createGain(), sideToR = ctx.createGain();
      inG.connect(split);
      split.connect(lMid, 0); split.connect(rMid, 1); lMid.connect(mid); rMid.connect(mid);
      split.connect(lSide, 0); split.connect(rSide, 1); lSide.connect(side); rSide.connect(side);
      side.connect(sideToL); side.connect(sideToR);
      mid.connect(merge, 0, 0); sideToL.connect(merge, 0, 0);
      mid.connect(merge, 0, 1); sideToR.connect(merge, 0, 1);
      const node: EffectNode = {
        input: inG, output: merge,
        update: (pp) => { const w = Number(pp.width ?? 1.3); sp(ctx, sideToL.gain, w); sp(ctx, sideToR.gain, -w); },
        dispose: () => [inG, split, merge, lMid, rMid, lSide, rSide, mid, side, sideToL, sideToR].forEach((n) => n.disconnect()),
      };
      node.update(p);
      return node;
    }
    case "denoise": {
      // Real-time spectral noise reduction in an AudioWorklet, using THIS insert's
      // own profile (keyed by fx.id) so tracks are independent. Needs the module
      // registered on this context (done at app startup for the live engine).
      // Offline render contexts don't register it → falls back to passthrough.
      // The dry path is delayed by the worklet's latency so Mix crossfades
      // wet/dry without comb-filtering (mirrors the Deverb insert).
      try {
        const id = fx.id;
        const inGain = ctx.createGain();
        const dry = ctx.createGain();
        const wet = ctx.createGain();
        const out = ctx.createGain();
        const dryDelay = ctx.createDelay(1);
        dryDelay.delayTime.value = DENOISE_LATENCY / ctx.sampleRate;
        const prof0 = getInsertProfile(id);
        const w = new AudioWorkletNode(ctx, "denoise-processor", {
          numberOfInputs: 1, numberOfOutputs: 1,
          channelCount: 2, channelCountMode: "explicit", channelInterpretation: "speakers",
          outputChannelCount: [2],
          processorOptions: { profileSamples: prof0 ? prof0.samples : null, sr: prof0 ? prof0.sr : ctx.sampleRate, strength: Number(p.strength ?? 0.7), smoothing: Number(p.smoothing ?? 0.5) },
        });
        inGain.connect(dry); dry.connect(dryDelay); dryDelay.connect(out);
        inGain.connect(w); w.connect(wet); wet.connect(out);
        let sentVersion = getInsertProfileVersion(id);
        const pushProfile = () => {
          const prof = getInsertProfile(id);
          if (prof && getInsertProfileVersion(id) !== sentVersion) {
            sentVersion = getInsertProfileVersion(id);
            w.port.postMessage({ type: "profile", samples: prof.samples, sr: prof.sr });
          }
        };
        const node: EffectNode = {
          input: inGain, output: out,
          update: (pp) => {
            w.port.postMessage({ type: "params", strength: Number(pp.strength ?? 0.7), smoothing: Number(pp.smoothing ?? 0.5) });
            const mix = Number(pp.mix ?? 1);
            sp(ctx, wet.gain, mix); sp(ctx, dry.gain, 1 - mix);
            pushProfile();
          },
          dispose: () => [inGain, dry, wet, out, dryDelay, w].forEach((n) => n.disconnect()),
        };
        pushProfile();
        node.update(p);
        return node;
      } catch {
        const g = ctx.createGain();
        return { input: g, output: g, update: () => {}, dispose: () => g.disconnect() };
      }
    }
    case "deverb": {
      // Real-time late-reverb suppression in an AudioWorklet (same engine as the
      // Deverb VST v1.1) — no profile to learn; the room is Decay/Damping/Reverb
      // Level, and Strength sets how hard the estimated tail is cut.
      // The dry path is delayed by the worklet's latency so Mix crossfades
      // wet/dry without comb-filtering (mirrors the Deverb VST's dry-delay ring).
      try {
        const inGain = ctx.createGain();
        const dry = ctx.createGain();
        const wet = ctx.createGain();
        const out = ctx.createGain();
        const dryDelay = ctx.createDelay(1);
        dryDelay.delayTime.value = DEVERB_LATENCY / ctx.sampleRate;
        const w = new AudioWorkletNode(ctx, "deverb-processor", {
          numberOfInputs: 1, numberOfOutputs: 1,
          channelCount: 2, channelCountMode: "explicit", channelInterpretation: "speakers",
          outputChannelCount: [2],
          processorOptions: {
            decay: Number(p.decay ?? 1.2),
            damping: Number(p.damping ?? 0.5),
            reverb: Number(p.reverb ?? -3),
            strength: Number(p.strength ?? 0.5),
          },
        });
        inGain.connect(dry); dry.connect(dryDelay); dryDelay.connect(out);
        inGain.connect(w); w.connect(wet); wet.connect(out);
        const node: EffectNode = {
          input: inGain,
          output: out,
          update: (pp) => {
            w.port.postMessage({
              type: "params",
              decay: Number(pp.decay ?? 1.2),
              damping: Number(pp.damping ?? 0.5),
              reverb: Number(pp.reverb ?? -3),
              strength: Number(pp.strength ?? 0.5),
            });
            const mix = Number(pp.mix ?? 0.85);
            sp(ctx, wet.gain, mix); sp(ctx, dry.gain, 1 - mix);
          },
          dispose: () => [inGain, dry, wet, out, dryDelay, w].forEach((n) => n.disconnect()),
        };
        node.update(p);
        return node;
      } catch {
        const g = ctx.createGain();
        return { input: g, output: g, update: () => {}, dispose: () => g.disconnect() };
      }
    }
    default: {
      // Fallback passthrough (also used while a WAM is loading).
      const g = ctx.createGain();
      return { input: g, output: g, update: () => {}, dispose: () => g.disconnect() };
    }
  }
}
